import BigNumber from 'bignumber.js'
import { BIG_ZERO } from 'config/constants/number'
import { Token } from 'config/types'
import Column from 'layout/Components/Column'
import { RowBetween } from 'layout/Components/Row'
import { useEffect, useRef } from 'react'
import { useTokenSuspendedByPage } from 'state/app/hooks'
import { useGetDisplayAmount } from 'state/profile/hooks'
import styled, { css } from 'styled-components'
import Box from 'UIKit/Box/Box'
import CircleLoader from 'UIKit/Loader/CircleLoader'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import TokenLogo from 'UIKit/TokenLogo'
import { getFullDisplayBalance } from 'utils/formatBalance'
import { getTokenName } from 'utils/token'
import { PaymentPageEnums } from 'views/Payment/Menu'
import { CurrencyDisplayInfo } from '../SelectedCurrencyItem'
import { PaymentWrapOption } from '../styles'

export type CurrencyRowItemProps = {
  currency: Token
  amount: BigNumber | null
  selectedToken?: Token
  onSelect: () => void
  fetchBalance: (token: Token) => Promise<BigNumber> | undefined
  hideNetwork?: boolean
  from: PaymentPageEnums
}

const CurrencyRowItem: React.FC<CurrencyRowItemProps> = ({
  currency,
  amount,
  onSelect,
  fetchBalance,
  hideNetwork = true,
  from,
}) => {
  const isBalanceFetchedRef = useRef(false)
  const { displayAmount, symbol, selectedFiat, isDisplayInFiat } = useGetDisplayAmount({
    token: currency,
    amount: amount || BIG_ZERO,
  })

  useEffect(() => {
    if (currency && amount === undefined && !isBalanceFetchedRef.current && fetchBalance) {
      isBalanceFetchedRef.current = true
      fetchBalance(currency)
    }
  }, [fetchBalance, currency.code])

  const priceDisplay = getFullDisplayBalance(
    Number(displayAmount),
    0,
    isDisplayInFiat ? selectedFiat.displayDecimals : 6,
  )

  const { isSuspended } = useTokenSuspendedByPage(currency, from)

  return (
    <WrapperCurrencyRow
      isSmallBalance={fetchBalance && amount && amount !== BIG_ZERO && amount.eq(0)}
      // selected={hideNetwork ? selectedToken?.code === currency.code : checkTokenEqual(selectedToken, currency)}
      onClick={onSelect}
      gap="12px"
    >
      <Box minWidth="32px">
        <TokenLogo
          token={currency}
          size={32}
          hideNetwork={hideNetwork}
          networkImageProps={{
            width: 16,
            height: 16,
          }}
        />
      </Box>
      <RowBetween>
        <Column>
          <CurrencyDisplayInfo token={currency} hideNetwork={hideNetwork} />
          <Text
            fontSize="12px"
            lineHeight="20px"
            letterSpacing={'-0.23px'}
            fontWeight={500}
            color="var(--pm-text-note-color)"
          >
            {currency.displayName || getTokenName(currency)}
          </Text>
        </Column>
        <Column gap="4px">
          {isSuspended ? (
            <TransText fontSize="12px" color="textAlt699" textAlign="right">
              Suspended
            </TransText>
          ) : (
            amount &&
            amount !== BIG_ZERO && (
              <>
                <Text fontSize="14px" fontWeight="600" color="white" textAlign="right">
                  {amount ? <>{getFullDisplayBalance(amount, 0, 6)}</> : <CircleLoader />}
                </Text>
                <Text fontSize="12px" color="textAlt699" textAlign="right">
                  {displayAmount ? (
                    <>
                      {symbol}
                      {isDisplayInFiat
                        ? displayAmount.gt(0)
                          ? priceDisplay === '0'
                            ? '0.00'
                            : priceDisplay
                          : 0
                        : ''}
                    </>
                  ) : (
                    <CircleLoader />
                  )}
                </Text>
              </>
            )
          )}
        </Column>
      </RowBetween>
    </WrapperCurrencyRow>
  )
}
const WrapperCurrencyRow = styled(PaymentWrapOption)<{ selected?: boolean; isSmallBalance: boolean }>`
  ${({ isSmallBalance }) =>
    isSmallBalance &&
    css`
      opacity: 0.42;
    `}
`
export default CurrencyRowItem
